import axios from "axios";
import { useAuth } from "@/store/auth";
export const api = axios.create({
    // Весь бизнес-API живёт под /api/*. Клиентский код может писать
    // относительные пути вида "/auth/login" — axios припишет префикс.
    baseURL: "/api",
    withCredentials: true,
    timeout: 15_000,
});
api.interceptors.request.use((config) => {
    const token = useAuth.getState().tokens?.accessToken;
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});
// Single-flight refresh — share across concurrent 401s.
let refreshPromise = null;
// Экспортирован для «тихого входа» LoginPage: single-flight гарантирует, что
// StrictMode-двойной mount даст ОДИН HTTP-запрос и одну ротацию jti
// (grace-window 90с на бэке — страховка кросс-контекстных гонок, не первая
// линия). Прямой вызов при провале стор НЕ чистит (clear() — только в
// 401-ветке интерцептора ниже).
export async function refreshAccess() {
    if (refreshPromise)
        return refreshPromise;
    refreshPromise = (async () => {
        try {
            // Через тот же `api` instance с флагом _noRefresh: это одновременно
            // (а) не зацикливает интерцептор при 401 и (б) наследует baseURL,
            // withCredentials и другие общие настройки.
            const resp = await api.post("/auth/refresh", {}, { _noRefresh: true });
            const t = resp.data;
            useAuth.getState().setTokens({
                accessToken: t.access_token,
                accessExpiresAt: t.access_expires_at,
            });
            return t.access_token;
        }
        finally {
            refreshPromise = null;
        }
    })();
    return refreshPromise;
}
const REFRESHABLE_METHODS = new Set(["get", "post", "patch", "put", "delete"]);
api.interceptors.response.use((r) => r, async (error) => {
    const original = error.config;
    if (!original || original._retry || original._noRefresh) {
        throw error;
    }
    if (error.response?.status === 401 &&
        REFRESHABLE_METHODS.has((original.method ?? "get").toLowerCase())) {
        try {
            const newAccess = await refreshAccess();
            original._retry = true;
            original.headers = original.headers ?? {};
            original.headers.Authorization = `Bearer ${newAccess}`;
            return api.request(original);
        }
        catch {
            useAuth.getState().clear();
            // ...и увести на форму входа. Без этого вкладка становится «зомби»:
            // `clear()` не наблюдает никто (useAuth подписан только в LoginPage), а
            // React Query при ошибке держит прошлые data — страница выглядит живой,
            // показывает устаревший профиль и молча долбит по два 401 в минуту, пока
            // человек не кликнет по меню. Раньше это было экзотикой; смена email
            // гасит текущую сессию штатно, и случай стал рядовым.
            // Полная навигация, а не SPA: заодно обнуляет кеш и всё in-memory.
            // Петли нет — refreshAccess ходит с _noRefresh, а на /login запросов нет.
            if (typeof window !== "undefined") {
                window.location.assign("/login?prompt=login&reason=session_ended");
            }
        }
    }
    throw error;
});
